import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Monitor, Smartphone, Terminal, MessageCircle, Code, Scan, DoorOpen, Printer, TabletSmartphone, CheckCircle2, Server, Cpu, Satellite, Radio } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const plataformas = [{
  icon: <Monitor className="w-8 h-8 text-emerald-400" data-id="element-527" />,
  title: 'Portal Web Institucional',
  description: 'Plataforma web robusta y marca blanca para la venta de entradas, gestión de socios y administración completa de tu organización.',
  features: ['Diseño responsive', 'Marca blanca (White-label)', 'Panel de administración en tiempo real', 'Pasarelas de pago integradas']
}, {
  icon: <Smartphone className="w-8 h-8 text-emerald-400" data-id="element-528" />,
  title: 'App Móvil (iOS & Android)',
  description: 'Aplicación nativa personalizada para tus usuarios, permitiendo llevar sus entradas, credenciales y wallet digital en su bolsillo.',
  features: ['Credenciales digitales', 'Notificaciones Push', 'Wallet digital', 'Transferencia de tickets']
}, {
  icon: <Terminal className="w-8 h-8 text-emerald-400" data-id="element-529" />,
  title: 'Software de Autoatención',
  description: 'Interfaz optimizada para pantallas táctiles que permite a los usuarios comprar entradas o gestionar su membresía de forma autónoma.',
  features: ['Interfaz intuitiva', 'Flujo de compra rápido', 'Integración con POS', 'Emisión de tickets térmicos']
}, {
  icon: <MessageCircle className="w-8 h-8 text-emerald-400" data-id="element-530" />,
  title: 'Venta por WhatsApp',
  description: 'Canal de ventas automatizado a través de WhatsApp, acercando la compra de entradas a la aplicación de mensajería más usada.',
  features: ['Chatbot inteligente', 'Envío de tickets en PDF/QR', 'Atención 24/7', 'Respuestas automatizadas']
}, {
  icon: <Code className="w-8 h-8 text-emerald-400" data-id="element-531" />,
  title: 'API & Integraciones',
  description: 'Conecta TicketMaestro con tu ecosistema tecnológico actual. Sincroniza datos con tu CRM, ERP o sistemas contables.',
  features: ['API RESTful', 'Webhooks en tiempo real', 'Documentación para desarrolladores', 'Soporte técnico especializado']
}];
const equipamientos = [{
  icon: <Scan className="w-8 h-8 text-emerald-400" data-id="element-532" />,
  title: 'Lectores QR Profesionales',
  description: 'Escáneres de alta velocidad diseñados para lectura rápida en condiciones de alta demanda y baja luminosidad.',
  features: ['Lectura en milisegundos', 'Pantalla de validación', 'Batería de larga duración', 'Conectividad WiFi/4G']
}, {
  icon: <DoorOpen className="w-8 h-8 text-emerald-400" data-id="element-533" />,
  title: 'Torniquetes y Molinetes',
  description: 'Control de acceso físico automatizado e integrado directamente con nuestra plataforma para validación en tiempo real.',
  features: ['Integración nativa', 'Control de aforo', 'Alta resistencia', 'Indicadores LED de paso']
}, {
  icon: <Server className="w-8 h-8 text-emerald-400" data-id="element-534" />,
  title: 'Kioscos Físicos (Tótems)',
  description: 'Estructuras robustas con pantalla táctil para autoatención en estadios, recintos y puntos de venta estratégicos.',
  features: ['Pantallas antivandálicas', 'Impresora térmica integrada', 'Lector de tarjetas/POS', 'Diseño personalizable']
}, {
  icon: <Printer className="w-8 h-8 text-emerald-400" data-id="element-535" />,
  title: 'Equipamiento de Boletería',
  description: 'Todo lo necesario para operar puntos de venta físicos con la máxima eficiencia y seguridad.',
  features: ['Impresoras térmicas de tickets', 'Gavetas de dinero', 'Lectores de código de barras', 'Terminales punto de venta (POS)']
}, {
  icon: <TabletSmartphone className="w-8 h-8 text-emerald-400" data-id="element-536" />,
  title: 'Dispositivos Móviles (Handhelds)',
  description: 'Terminales portátiles robustas para el personal de control de acceso y validación de entradas en cualquier punto del recinto.',
  features: ['Lector láser integrado', 'Resistencia a caídas y agua', 'Conectividad WiFi/4G', 'App de validación preinstalada']
}, {
  icon: <Satellite className="w-8 h-8 text-emerald-400" data-id="element-537" />,
  title: 'Provisión de Internet Satelital',
  description: 'Conectividad garantizada en cualquier ubicación, sin depender de infraestructura terrestre. Ideal para recintos remotos o eventos al aire libre.',
  features: ['Conectividad para las plataformas operativas del evento', 'Soporte para transmisiones en vivo y streaming', 'Internet dedicado para los asistentes del evento', 'Cobertura en zonas sin infraestructura terrestre']
}, {
  icon: <Radio className="w-8 h-8 text-emerald-400" data-id="element-538" />,
  title: 'Equipamiento Operativo',
  description: 'Equipos de comunicación y logística para coordinar al personal del evento de forma eficiente y segura en todo momento.',
  features: ['Radios de comunicación por radiofrecuencia (VHF/UHF)', 'Auriculares y accesorios para el personal', 'Cargadores y baterías de respaldo', 'Chalecos y credenciales de identificación del staff']
}];
export function PlataformasPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.hash]);
  return <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-emerald-500/30" data-id="element-539">
      <Navbar data-id="element-540" />

      <main data-id="element-541">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-id="element-542">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/20 via-slate-950 to-slate-950 -z-10" data-id="element-543" />

          <div className="container mx-auto px-4 md:px-6" data-id="element-544">
            <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeInUp} data-id="element-545">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6" data-id="element-546">
                <Cpu className="w-4 h-4" data-id="element-547" />
                Tecnología e Infraestructura
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-548">
                Plataformas y{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400" data-id="element-549">
                  Equipamientos
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto" data-id="element-550">
                Tecnología de punta y hardware especializado para llevar la
                gestión de tus eventos, recintos y organizaciones al siguiente
                nivel. Ecosistema 100% integrado.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Plataformas Section */}
        <section id="ecosistema-software" className="py-20 bg-slate-900/50 border-y border-slate-800/50 scroll-mt-20" data-id="element-551">
          <div className="container mx-auto px-4 md:px-6" data-id="element-552">
            <motion.div className="mb-16 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeInUp} data-id="element-553">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-554">
                Ecosistema de Software
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-555">
                Múltiples canales de venta y gestión conectados a un único
                núcleo centralizado para un control total.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={staggerContainer} data-id="element-556">
              {plataformas.map((item, index) => <motion.div key={index} variants={fadeInUp} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-emerald-500/30 transition-colors group" data-id="element-557">
                  <div className="mb-6 p-4 bg-slate-950 rounded-xl inline-block border border-slate-800 group-hover:border-emerald-500/30 transition-colors" data-id="element-558">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-slate-100" data-id="element-559">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 mb-6 leading-relaxed text-sm" data-id="element-560">
                    {item.description}
                  </p>
                  <ul className="space-y-3" data-id="element-561">
                    {item.features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-300" data-id="element-562">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" data-id="element-563" />
                        <span data-id="element-564">{feature}</span>
                      </li>)}
                  </ul>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Equipamiento Section */}
        <section id="hardware-equipamiento" className="py-20 scroll-mt-20" data-id="element-565">
          <div className="container mx-auto px-4 md:px-6" data-id="element-566">
            <motion.div className="mb-16 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeInUp} data-id="element-567">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-568">
                Hardware y Equipamiento
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-569">
                Dispositivos físicos de grado industrial preparados para
                soportar la exigencia de eventos masivos y uso continuo.
              </p>
            </motion.div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={staggerContainer} data-id="element-570">
              {equipamientos.map((item, index) => <motion.div key={index} variants={fadeInUp} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-8 hover:bg-slate-900 transition-colors" data-id="element-571">
                  <div className="mb-6 flex items-center gap-4" data-id="element-572">
                    <div className="p-3 bg-emerald-500/10 rounded-lg text-emerald-400" data-id="element-573">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-100" data-id="element-574">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-400 mb-6 leading-relaxed text-sm" data-id="element-575">
                    {item.description}
                  </p>
                  <ul className="space-y-3" data-id="element-576">
                    {item.features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-300" data-id="element-577">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-2" data-id="element-578" />
                        <span data-id="element-579">{feature}</span>
                      </li>)}
                  </ul>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden" data-id="element-580">
          <div className="absolute inset-0 bg-emerald-900/20" data-id="element-581" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent" data-id="element-582" />

          <div className="container mx-auto px-4 relative z-10" data-id="element-583">
            <motion.div className="max-w-3xl mx-auto text-center bg-slate-900/80 backdrop-blur-xl border border-slate-700 p-10 md:p-16 rounded-3xl shadow-2xl" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-584">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-585">
                ¿Necesitas equipar tu recinto?
              </h2>
              <p className="text-lg text-slate-300 mb-8" data-id="element-586">
                Nuestro equipo de expertos te asesorará para encontrar la
                combinación perfecta de software y hardware para tus necesidades
                específicas.
              </p>
              <Button variant="primary" size="lg" className="text-lg px-8 py-4" onClick={() => window.location.href = 'mailto:contacto@ticketmaestro.net'} data-id="element-587">
                Contactar Ventas
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-588" />
    </div>;
}